import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (email, password, name) => api.post('/auth/register', { email, password, name }),
  verify: () => api.get('/auth/verify'),
};

// Planner API
export const plannerAPI = {
  // Generate calendar from file
  generateFromFile: async (file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Send all options
    if (options.generateContent !== undefined) {
      formData.append('generateContent', String(options.generateContent));
    }
    if (options.weekStartDate) {
      formData.append('weekStartDate', options.weekStartDate);
    }
    if (options.personaCount) {
      formData.append('personaCount', String(options.personaCount));
    }
    if (options.postsPerWeek) {
      formData.append('postsPerWeek', String(options.postsPerWeek));
    }
    if (options.commentsPerPost) {
      formData.append('commentsPerPost', String(options.commentsPerPost));
    }
    
    return api.post('/planner/generate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Generate calendar from JSON
  generateFromJson: (input, options = {}) => 
    api.post('/planner/generate/json', { input, options }),
  
  // Get calendar by ID
  getCalendar: (id) => api.get(`/planner/calendar/${id}`),
  
  // List all calendars
  listCalendars: () => api.get('/planner/calendars'),
  
  // Derive next week
  deriveNextWeek: (id, options = {}) => 
    api.post(`/planner/calendar/${id}/next-week`, options),
  
  // Update calendar item
  updateItem: (calendarId, itemId, updates) => 
    api.patch(`/planner/calendar/${calendarId}/item/${itemId}`, updates),
  
  // Regenerate item
  regenerateItem: (calendarId, itemId) => 
    api.post(`/planner/calendar/${calendarId}/item/${itemId}/regenerate`),
  
  // Approve item
  approveItem: (calendarId, itemId) => 
    api.post(`/planner/calendar/${calendarId}/item/${itemId}/approve`),
  
  // Parse file (preview)
  parseFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/planner/parse', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  
  // Get validation report
  getValidation: (id) => api.get(`/planner/calendar/${id}/validation`),
  
  // Delete calendar
  deleteCalendar: (id) => api.delete(`/planner/calendar/${id}`),
  
  // Get dashboard stats
  getStats: () => api.get('/planner/stats'),
  
  // Get recent runs
  getRecentRuns: () => api.get('/planner/recent'),
};

// Export API
export const exportAPI = {
  exportCSV: (calendarData, filename) => 
    api.post('/export/csv', { calendarData, filename }, { responseType: 'blob' }),
  
  exportXLSX: (calendarData, filename) => 
    api.post('/export/xlsx', { calendarData, filename }, { responseType: 'blob' }),
  
  exportJSON: (calendarData, filename, pretty = true) => 
    api.post('/export/json', { calendarData, filename, pretty }, { responseType: 'blob' }),
  
  exportPDF: (calendarData, filename) => 
    api.post('/export/pdf', { calendarData, filename }, { responseType: 'blob' }),
  
  getPreview: (calendarData, format = 'csv', limit = 3) => 
    api.post('/export/preview', { calendarData, format, limit }),
  
  getSummary: (calendarData) => 
    api.post('/export/summary', { calendarData }),
  
  // Export calendar by ID
  exportCalendar: (id, format) => 
    api.get(`/export/calendar/${id}`, { 
      params: { format },
      responseType: format === 'json' ? 'json' : 'blob' 
    }),
};

// Simulator API
export const simulatorAPI = {
  simulateThread: (item, skipLLM = false) => 
    api.post('/simulator/thread', { item, skipLLM }),
  
  simulateCalendar: (items, skipLLM = false) => 
    api.post('/simulator/calendar', { items, skipLLM }),
  
  getAnimation: (item) => 
    api.post('/simulator/animate', { item }),
  
  qualityCheck: (item) => 
    api.post('/simulator/quality-check', { item }),
  
  batchQualityCheck: (items) => 
    api.post('/simulator/quality-check/batch', { items }),
  
  // Simulate by calendar and item ID
  simulate: (calendarId, itemId) =>
    api.post(`/simulator/calendar/${calendarId}/item/${itemId}`),
};

// Helper to download blob
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export default api;
